'use client'

import { ArrowLeft, FileUp, Loader2, UploadCloud } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { ImportableStop } from '@/lib/import/types'
import { clientErrorMessage } from './extract-error'

/**
 * The detected-sheet description echoed back by `/api/import/extract` for
 * spreadsheet uploads (mirrors `describeSheet`'s return shape). Present ONLY for
 * spreadsheets with a recognizable header.
 */
interface SheetDescription {
	headers: string[]
	headerIndex: number
	detected: { city?: number; date?: number; venue?: number; state?: number }
	sampleRows: string[][]
}

/**
 * Accept filter for the picker — CSV/TSV text only. Excel workbooks must be
 * saved as CSV and PDFs go through Paste; the server refuses both with that
 * hint (drag-and-drop bypasses this filter).
 */
const ACCEPT = '.csv,.tsv,.txt'

/** The four logical columns the remap UI lets a user assign. */
const COLUMN_ROLES = [
	{ key: 'city', label: 'City', required: true },
	{ key: 'date', label: 'Date', required: false },
	{ key: 'venue', label: 'Venue', required: false },
	{ key: 'state', label: 'State', required: false },
] as const

/** A chosen column mapping — each role is a header index, or undefined for "none". */
type ChosenMap = { city?: number; date?: number; venue?: number; state?: number }

/** Generic, user-facing failure copy when the extract call fails outright. */
const EXTRACT_ERROR = 'Something went wrong reading that file — please try again.'
/** Shown when the file was readable but named no stops and had no remappable sheet. */
const UNREADABLE_ERROR = "Couldn't read that file"

/**
 * The successful payload from `/api/import/extract`. `sheet` rides along only
 * for spreadsheets with a detectable header (per the import contract).
 */
interface ExtractPayload {
	stops: ImportableStop[]
	sheet?: SheetDescription
}

/** Outcome of an extract POST: a payload, a user-facing error, or ignore (stale). */
type ExtractResult =
	| { kind: 'ok'; payload: ExtractPayload }
	| { kind: 'error'; message?: string }
	| { kind: 'ignore' }

/**
 * POST a file (optionally with a column-map override) to the shared extract
 * route. Pulls the branchy fetch/guard logic out of the component so its
 * handlers stay under the complexity ceiling. `columnMap` is the
 * already-stringified override field for a manual remap; omit it for the first
 * (auto-detect) pass. Returns `ignore` when superseded or aborted.
 */
async function extractFromFile(
	file: File,
	signal: AbortSignal,
	columnMap?: string,
): Promise<ExtractResult> {
	try {
		const form = new FormData()
		form.append('file', file)
		if (columnMap) {
			form.append('columnMap', columnMap)
		}
		const res = await fetch('/api/import/extract', { method: 'POST', body: form, signal })
		if (!res.ok) {
			return { kind: 'error', message: (await clientErrorMessage(res)) ?? undefined }
		}
		const data = (await res.json()) as ExtractPayload
		return { kind: 'ok', payload: { stops: data.stops ?? [], sheet: data.sheet } }
	} catch (err) {
		if (err instanceof DOMException && err.name === 'AbortError') {
			return { kind: 'ignore' }
		}
		return { kind: 'error' }
	}
}

/**
 * Render the detected/chosen mapping human-readably, e.g.
 * "City = Market, Date = Show Date, Venue = Room". Unmapped roles are omitted.
 * Header text is user-controlled spreadsheet data — returned as a plain string
 * and rendered as React children downstream (auto-escaped).
 */
function describeMapping(headers: string[], map: ChosenMap): string {
	const parts: string[] = []
	for (const role of COLUMN_ROLES) {
		const index = map[role.key]
		if (index !== undefined && headers[index]) {
			parts.push(`${role.label} = ${headers[index]}`)
		}
	}
	return parts.join(', ')
}

/** Dashed-border drop zone + a "Choose file" trigger. Pure presentation. */
function DropZone({
	onFiles,
	onChoose,
	inputRef,
	dragging,
	setDragging,
}: {
	onFiles: (files: FileList | null) => void
	onChoose: () => void
	inputRef: React.RefObject<HTMLInputElement | null>
	dragging: boolean
	setDragging: (value: boolean) => void
}) {
	return (
		<button
			type="button"
			onClick={onChoose}
			onDragOver={(event) => {
				event.preventDefault()
				setDragging(true)
			}}
			onDragLeave={() => setDragging(false)}
			onDrop={(event) => {
				event.preventDefault()
				setDragging(false)
				onFiles(event.dataTransfer.files)
			}}
			className={`flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center transition-colors ${
				dragging ? 'border-primary bg-surface-hover' : 'border-border hover:bg-surface-hover'
			}`}
		>
			<UploadCloud className="size-7 text-primary" aria-hidden />
			<span className="text-sm font-medium text-text-primary">Drop a file here, or choose one</span>
			<span className="text-xs text-text-muted">
				CSV · from Excel: File → Save As → CSV · for a PDF, use Paste
			</span>
			<span className="mt-1 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-text-secondary">
				<FileUp className="size-4" aria-hidden />
				Choose file
			</span>
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPT}
				className="hidden"
				onChange={(event) => onFiles(event.target.files)}
			/>
		</button>
	)
}

/**
 * One render-ready column for the preview table: a stable React key plus its
 * display label. WHY precompute the key here (not inline in JSX): the header
 * array is a fixed, never-reordered parse result, so position IS the column's
 * identity — but composing the key at the map call-site keeps the index out of
 * the JSX entirely, sidestepping the array-index-key heuristic cleanly rather
 * than suppressing it.
 */
interface PreviewColumn {
	key: string
	label: string
	index: number
}

/** A render-ready preview row: a stable key plus its already-aligned cells. */
interface PreviewRow {
	key: string
	cells: { key: string; value: string }[]
}

/** Build the keyed column descriptors from the (fixed) header row. */
function toPreviewColumns(headers: string[]): PreviewColumn[] {
	return headers.map((header, index) => ({
		key: `${header}-${index}`,
		label: header || `Col ${index + 1}`,
		index,
	}))
}

/** Build keyed preview rows, aligning each row's cells to the column order. */
function toPreviewRows(columns: PreviewColumn[], rows: string[][]): PreviewRow[] {
	return rows.slice(0, 3).map((row, rowIndex) => ({
		key: `${row.join('|')}-${rowIndex}`,
		cells: columns.map((column, colIndex) => ({
			key: `${column.key}-cell`,
			value: row[colIndex] ?? '',
		})),
	}))
}

/** A small preview table of the sheet's first sample rows, to orient the remap. */
function PreviewTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
	const columns = toPreviewColumns(headers)
	const previewRows = toPreviewRows(columns, rows)
	return (
		<div className="w-full min-w-0 overflow-x-auto rounded-md border border-border">
			<table className="w-full border-collapse text-left text-xs">
				<thead>
					<tr className="border-b border-border bg-surface-hover">
						{columns.map((column) => (
							<th
								key={column.key}
								className="whitespace-nowrap px-2 py-1.5 font-medium text-text-secondary"
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{previewRows.map((row) => (
						<tr key={row.key} className="border-b border-border last:border-0">
							{row.cells.map((cell) => (
								<td key={cell.key} className="whitespace-nowrap px-2 py-1.5 text-text-muted">
									{cell.value}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

/** One labeled role selector — a native select over the sheet headers plus "—". */
function RoleSelect({
	role,
	headers,
	value,
	onChange,
}: {
	role: (typeof COLUMN_ROLES)[number]
	headers: string[]
	value: number | undefined
	onChange: (index: number | undefined) => void
}) {
	const selectId = `colmap-${role.key}`
	const options = toPreviewColumns(headers)
	return (
		<label htmlFor={selectId} className="flex min-w-0 flex-col gap-1">
			<span className="text-xs font-medium text-text-secondary">
				{role.label}
				{role.required && <span className="ml-1 text-error-fg">*</span>}
			</span>
			<select
				id={selectId}
				value={value === undefined ? '' : String(value)}
				onChange={(event) => {
					const raw = event.target.value
					onChange(raw === '' ? undefined : Number(raw))
				}}
				className="w-full min-w-0 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary outline-none focus-visible:border-primary"
			>
				<option value="">—</option>
				{options.map((option) => (
					<option key={option.key} value={String(option.index)}>
						{option.label}
					</option>
				))}
			</select>
		</label>
	)
}

/**
 * The column-mapping panel: a role selector per logical column, a preview of the
 * sheet's sample rows, and an "Apply mapping" action. City is required to apply.
 * Owns only the in-progress `chosen` map; applying is delegated upward.
 */
function ColumnMapPanel({
	sheet,
	onApply,
	applying,
}: {
	sheet: SheetDescription
	onApply: (map: ChosenMap) => void
	applying: boolean
}) {
	const [chosen, setChosen] = useState<ChosenMap>(() => ({ ...sheet.detected }))

	const setRole = useCallback((key: keyof ChosenMap, index: number | undefined) => {
		setChosen((prev) => ({ ...prev, [key]: index }))
	}, [])

	const canApply = chosen.city !== undefined && !applying

	return (
		<div className="flex min-w-0 flex-col gap-3">
			<div className="grid min-w-0 grid-cols-2 gap-3">
				{COLUMN_ROLES.map((role) => (
					<RoleSelect
						key={role.key}
						role={role}
						headers={sheet.headers}
						value={chosen[role.key]}
						onChange={(index) => setRole(role.key, index)}
					/>
				))}
			</div>

			<PreviewTable headers={sheet.headers} rows={sheet.sampleRows} />

			{chosen.city === undefined && (
				<p className="text-xs text-error-fg">Pick a City column to apply the mapping.</p>
			)}

			<Button
				type="button"
				variant="default"
				size="sm"
				className="self-start"
				onClick={() => onApply(chosen)}
				disabled={!canApply}
			>
				{applying ? (
					<>
						<Loader2 className="size-4 animate-spin" aria-hidden />
						Applying…
					</>
				) : (
					'Apply mapping'
				)}
			</Button>
		</div>
	)
}

/**
 * The post-parse summary: "Found N stops", the detected mapping (spreadsheets
 * only), and the Continue / Adjust-columns actions. When `sheet` is absent
 * (no recognizable header) only Continue is shown — there is nothing to remap.
 */
function SummaryCard({
	stops,
	sheet,
	onContinue,
	onAdjust,
	showMapping,
	mapPanel,
}: {
	stops: ImportableStop[]
	sheet: SheetDescription | undefined
	onContinue: () => void
	onAdjust: () => void
	showMapping: boolean
	mapPanel: React.ReactNode
}) {
	const mapping = sheet ? describeMapping(sheet.headers, sheet.detected) : ''
	return (
		<div className="flex min-w-0 flex-col gap-3 rounded-lg border border-border p-4">
			<p className="text-sm font-medium text-text-primary">
				Found {stops.length} {stops.length === 1 ? 'stop' : 'stops'}
			</p>
			{sheet && mapping && <p className="text-xs text-text-muted">{mapping}</p>}

			{showMapping && mapPanel}

			{!showMapping && (
				<div className="flex flex-wrap items-center gap-2">
					<Button type="button" variant="default" size="sm" onClick={onContinue}>
						{sheet ? 'Looks right — Continue' : 'Continue'}
					</Button>
					{sheet && (
						<Button type="button" variant="outline" size="sm" onClick={onAdjust}>
							Adjust columns
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

interface UploadStepProps {
	/**
	 * Fired with the extracted stops and a `doc:<fileName>` source key when the
	 * user accepts a parse. `sourceKey` keys the per-source imported-tracker.
	 */
	onStops: (stops: ImportableStop[], sourceKey: string) => void
	/** Returns to the source picker. */
	onBack: () => void
}

/** Loading placeholder — a few row skeletons while a file is parsing. */
function ParseSkeleton() {
	return (
		<div className="flex flex-col gap-2" aria-hidden>
			<Skeleton className="h-5 w-32 rounded-md" />
			<Skeleton className="h-20 w-full rounded-lg" />
		</div>
	)
}

/**
 * Step 'upload' of the import wizard: turn an uploaded file into stops, with a
 * column-mapping fallback for idiosyncratic spreadsheets.
 *
 * Flow:
 *   1. Pick/drop a CSV file -> POST it to `/api/import/extract`.
 *   2. A readable result shows a summary ("Found N stops") and the detected
 *      column mapping (spreadsheets only). Continue accepts the parse; "Adjust
 *      columns" opens the remap panel.
 *   3. Applying a remap re-POSTs the SAME file with an explicit `columnMap`
 *      override and replaces the summary with the remapped result.
 *   4. A result with no `sheet` skips mapping entirely — just Continue.
 *
 * All fetches are guarded by a sequence + abort ref so an out-of-order or
 * superseded response never commits. Errors surface inline with a Retry; the
 * file handle is kept so a remap/retry never needs a re-pick.
 */
export function UploadStep({ onStops, onBack }: UploadStepProps) {
	const [file, setFile] = useState<File | null>(null)
	const [payload, setPayload] = useState<ExtractPayload | null>(null)
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [showMapping, setShowMapping] = useState(false)
	const [dragging, setDragging] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const inputRef = useRef<HTMLInputElement>(null)
	// Each parse claims the next number; only the latest may commit results.
	const seqRef = useRef(0)
	const abortRef = useRef<AbortController | null>(null)

	/**
	 * Run an extract for `target`, optionally with a column-map override, and
	 * commit the outcome. Shared by the first auto-detect pass and every remap.
	 * `isRemap` flips the right busy flag (Skeleton vs. inline "Applying…").
	 */
	const runExtract = useCallback(async (target: File, columnMap: string | undefined) => {
		abortRef.current?.abort()
		const controller = new AbortController()
		abortRef.current = controller
		const seq = ++seqRef.current

		const isRemap = columnMap !== undefined
		if (isRemap) {
			setApplying(true)
		} else {
			setLoading(true)
		}
		setError(null)

		const result = await extractFromFile(target, controller.signal, columnMap)
		// A newer parse superseded this one while it was in flight — ignore.
		if (seq !== seqRef.current || result.kind === 'ignore') {
			return
		}

		if (result.kind === 'error') {
			setError(result.message ?? EXTRACT_ERROR)
		} else if (result.payload.stops.length === 0 && !result.payload.sheet) {
			// Readable but no stops and nothing to remap — a dead end for this file.
			setError(UNREADABLE_ERROR)
		} else {
			setPayload(result.payload)
			setShowMapping(false)
		}
		setLoading(false)
		setApplying(false)
	}, [])

	const handleFiles = useCallback(
		(files: FileList | null) => {
			const chosen = files?.[0]
			if (!chosen) {
				return
			}
			setFile(chosen)
			setPayload(null)
			runExtract(chosen, undefined)
		},
		[runExtract],
	)

	const applyMapping = useCallback(
		(map: ChosenMap) => {
			if (!file || !payload?.sheet) {
				return
			}
			const columnMap = JSON.stringify({ headerIndex: payload.sheet.headerIndex, ...map })
			runExtract(file, columnMap)
		},
		[file, payload, runExtract],
	)

	const handleContinue = useCallback(() => {
		if (!payload) {
			return
		}
		const sourceKey = `doc:${file?.name ?? 'upload'}`
		onStops(payload.stops, sourceKey)
	}, [payload, file, onStops])

	const retry = useCallback(() => {
		if (file) {
			runExtract(file, undefined)
		} else {
			setError(null)
		}
	}, [file, runExtract])

	return (
		<div className="flex min-w-0 flex-col gap-3">
			{!payload && !loading && !error && (
				<DropZone
					onFiles={handleFiles}
					onChoose={() => inputRef.current?.click()}
					inputRef={inputRef}
					dragging={dragging}
					setDragging={setDragging}
				/>
			)}

			{loading && <ParseSkeleton />}

			{error && (
				<div className="flex flex-col items-start gap-3">
					<p aria-live="polite" className="text-sm text-error-fg">
						{error}
					</p>
					<Button type="button" variant="outline" size="sm" onClick={retry}>
						{file ? 'Retry' : 'Try again'}
					</Button>
				</div>
			)}

			{payload && !loading && (
				<SummaryCard
					stops={payload.stops}
					sheet={payload.sheet}
					onContinue={handleContinue}
					onAdjust={() => setShowMapping(true)}
					showMapping={showMapping}
					mapPanel={
						payload.sheet && (
							<ColumnMapPanel sheet={payload.sheet} onApply={applyMapping} applying={applying} />
						)
					}
				/>
			)}

			<Button
				type="button"
				variant="ghost"
				size="sm"
				className="self-start"
				onClick={onBack}
				disabled={loading || applying}
			>
				<ArrowLeft className="size-4" aria-hidden />
				Back
			</Button>
		</div>
	)
}
